import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ActionOrigin,
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import isProcedureVersionPoW from 'procedures/design/utils/isProcedureVersionPoW';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import buildFullExperimentPlan, {
  deriveConfirmationMethodByParamVersionId,
} from 'procedures/planning/buildFullExperimentPlan';
import {
  expandPlanDataParameterValuesForRest,
  expandPlanMaterialInputValuesForRest,
} from 'procedures/planning/multiValuePlateMap';
import {ExperimentalInputs, FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  ProcedureRunPlanDesignStateSummary,
  setProcedureRunPlanDesignState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {
  ConditionGroup,
  createDefaultConditionGroups,
  isDraftConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {
  optInToFlowchartProcedureRunDesign,
  optOutOfFlowchartProcedureRunDesign,
  shouldShowFlowchartProcedureRunDesign,
} from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDesignUtils';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';

import {StudyInaccessibleContentBanner} from '../../../utils/ui/StudyQueryBanners';
import ProcedureRunDesignFlowchartEditor from '../../flowcharts/design/ProcedureRunDesignFlowchartEditor';
import {useProcedureRunDesignFlowchartEdgeSpecs} from '../../flowcharts/design/useProcedureRunDesignFlowchartEdgeSpecs';
import {
  EditNumConditionGroupsButtons,
  planConditionGroupsButtonVariant,
} from '../conditions/EditNumConditionGroupsButtons';
import {ExperimentFlowchart, prepareInitialExperimentalInputs} from '../design-state/ExperimentalInputs';
import getStudyDesignFromProcedureRunPlan, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT,
} from '../design-state/getStudyDesignFromProcedureRunPlan';
import {
  collectPlanErrors,
  getConditionValidationTooltip,
  validateConditions,
} from '../design-state/procedureRunStudyValidationUtils';
import {useStudyDesignState} from '../design-state/useStudyDesignState';
import {useProcedureRunPlanContext} from '../overview/StudyOverviewContexts';
import EditProcedureModal, {
  EditProcedureModalMode,
  ProcedureChangeParams,
} from '../procedure-selection/EditProcedureModal';
import {preserveProcedureInputsOnVersionUpdate} from '../procedure-selection/preserveProcedureInputsOnVersionUpdate';
import PublishedProcedureVersionUpdateBanner from '../procedure-selection/PublishedProcedureVersionUpdateBanner';
import SelectedProcedureEditor from '../procedure-selection/SelectedProcedureEditor';
import {useProcedureVersionUpdateCheck} from '../procedure-selection/useProcedureVersionUpdateCheck';
import {
  MultiProcedureRunPlanStudyDesignTabContentsQuery,
  MultiProcedureRunPlanStudyDesignTabContentsQueryVariables,
  MultiProcedureRunPlanStudyDesignTabContentsQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
} from './__generated/MultiProcedureRunPlanStudyDesignTabContentsQuery';
import EmptyProcedureRunPlanDesignTabContents from './EmptyProcedureRunPlanDesignTabContents';
import {
  FlowchartProcedureRunDesignOptInBanner,
  FlowchartProcedureRunDesignOptOutBanner,
} from './FlowchartProcedureRunDesignPreviewBanners';
import ProcedureRunDesignEditorLeftSidepanel from './ProcedureRunDesignEditorLeftSidepanel';
import ProcedureRunDesignEditorRightSidepanel from './ProcedureRunDesignEditorRightSidepanel';
import {
  StudyDesignTabEditState,
  StudyDesignTabSaveButton,
  StudyDesignTabSavingIndication,
} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import {useFlowchartProcedureRunDesignPreviewState} from './useFlowchartProcedureRunDesignPreviewState';

// Indices for accessing typed values in the experiment plan
const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;

const AMOUNT_INDEX = 1;

function hasCompatiblePersistedMethodInputs(
  experimentalInputs: ExperimentalInputs,
  methodIds: Set<string>
): boolean {
  const persistedMethodIds = Object.keys(experimentalInputs);
  // Require an exact key-set match so that we never reuse persisted inputs
  // that are missing entries for current methods or contain obsolete ones.
  if (persistedMethodIds.length !== methodIds.size) {
    return false;
  }
  return persistedMethodIds.every((methodId) => methodIds.has(methodId));
}

function isCompatiblePersistedPlanDesignState(
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined,
  methodIds: Set<string>
): persistedPlanDesignState is ProcedureRunPlanDesignStateSummary {
  return (
    persistedPlanDesignState != null &&
    hasCompatiblePersistedMethodInputs(persistedPlanDesignState.experimentalInputs, methodIds)
  );
}

/**
 * Serializes experiment plan conditions to the procedure run PLAN REST format.
 * Note: This is different from serializeExperimentConditionsForNetwork which is for procedure RUNS.
 *
 * Plan REST API expects {value: TypedValue} for all types (data params, material inputs, equipment).
 * For material inputs, we nest both material and amount inside the value object.
 *
 * For parameters confirmed via plate map, the UI represents multiple setpoints as a single
 * multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK). This function expands those into
 * multiple `values[]` entries with one scalar TypedValue each, matching the contract of
 * serializeExperimentConditionsForNetwork and the backend planned-value upsert shape.
 */
export function serializePlanConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan,
  conditions: Array<ConditionGroup>
): Array<ProcedureRunPlanConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;

  return experimentPlanPerCondition.flatMap((methodPlans, i) => {
    const serializedMethodPlans = methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: expandPlanDataParameterValuesForRest(pv.values[VALUE_INDEX]),
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        values: expandPlanMaterialInputValuesForRest(pv.values[MATERIAL_INDEX], pv.values[AMOUNT_INDEX]),
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
        // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
        // than a missing key.
        values: [{value: {equipment: pv.values[VALUE_INDEX] ?? null}}],
      })),
    }));
    const conditionEntries = fanOutMethodPlansByConditionId(conditions[i], serializedMethodPlans);
    return conditionEntries.map(({id, templateMethodVersionId, methodPlan}) => ({
      ...(id != null ? {id} : {}),
      ...(templateMethodVersionId != null ? {templateMethodVersionId} : {}),
      name: namesPerCondition[i],
      description: descriptionsPerCondition[i],
      position: i,
      replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
        replicateNumber: j + 1,
      })),
      methodPlan,
    }));
  });
}

/**
 * Serializes preserved experimental inputs to REST format for procedure / version changes.
 *
 * Used when the plan's flowchart is being replaced (procedure swap or version update). The new
 * flowchart has a fresh set of PMDV ids, so this helper emits a base payload with one per-PMDV condition row
 * per (condition, new PMDV), including method plans and replicate counts derived from the current UI state.
 *
 * Callers are responsible for flowchart-change-specific post-processing, such as:
 * - assigning `id` for rows that should update existing conditions instead of being recreated
 * - deciding whether to clear or preserve `sourcePMEIIds` per emitted row
 *
 * @param preservedInputs - The experimental inputs with new method IDs and preserved values
 * @param conditions - The current conditions from the plan, used only for metadata
 *   (name/description/replicate count)
 * @returns Array of conditions in REST format ready for the API
 */
function serializePreservedInputsForNetwork(
  preservedInputs: ExperimentalInputs,
  conditions: Array<ConditionGroup>
): Array<ProcedureRunPlanConditionREST> {
  return conditions.flatMap((condition, conditionIdx) =>
    Object.entries(preservedInputs).map(([methodId, inputsForMethod]) => {
      const valuesForCondition = inputsForMethod.values[conditionIdx];
      return {
        name: condition.name,
        description: condition.description,
        position: conditionIdx,
        templateMethodVersionId: methodId,
        replicateInfos: Array.from({length: condition.numReplicates}, (_, j) => ({
          replicateNumber: j + 1,
        })),
        methodPlan: {
          methodVersionId: methodId,
          dataParameterValues: inputsForMethod.ids.parameters.map((paramVersionId, paramIdx) => ({
            dataParameterVersionId: paramVersionId,
            values: expandPlanDataParameterValuesForRest(
              valuesForCondition?.parameters[paramIdx]?.[VALUE_INDEX]
            ),
          })),
          materialInputValues: inputsForMethod.ids.materialInputs.map((materialVersionId, materialIdx) => ({
            materialInputVersionId: materialVersionId,
            values: expandPlanMaterialInputValuesForRest(
              valuesForCondition?.materialInputs[materialIdx]?.[MATERIAL_INDEX],
              valuesForCondition?.materialInputs[materialIdx]?.[AMOUNT_INDEX]
            ),
          })),
          equipmentValues: inputsForMethod.ids.equipment.map((equipmentVersionId, equipmentIdx) => ({
            equipmentVersionId,
            // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
            // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
            // than a missing key.
            values: [
              {
                value: {
                  equipment: valuesForCondition?.equipment[equipmentIdx]?.[VALUE_INDEX] ?? null,
                },
              },
            ],
          })),
        },
      };
    })
  );
}

/**
 * Builds per-condition mappings from new PMDV id to existing condition id for rows that can be preserved.
 *
 * Primary match: PMDV ID exists in both old and new flowcharts.
 * Fallback match: PMDV IDs differ but nodeId is stable across versions.
 */
function buildPreservedConditionIdMappings(
  conditions: Array<ConditionGroup>,
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): Array<Record<string, string>> {
  const oldNodeIdByMethodId = new Map(oldFlowchart.nodes.map((method) => [method.id, method.nodeId]));
  const newMethodIds = new Set(newFlowchart.nodes.map((method) => method.id));

  const newMethodIdByNodeId = new Map<string, string>();
  newFlowchart.nodes.forEach((method) => {
    newMethodIdByNodeId.set(method.nodeId, method.id);
  });

  return conditions.map((condition) => {
    const preservedConditionIdByNewMethodId: Record<string, string> = {};

    Object.entries(condition.conditionIdsByPmdvId).forEach(([oldMethodId, conditionId]) => {
      // A null id means there's no persisted condition row to preserve for this PMDV.
      if (conditionId == null) {
        return;
      }

      // The same PMDV exists in both old and new flowcharts
      if (newMethodIds.has(oldMethodId)) {
        preservedConditionIdByNewMethodId[oldMethodId] = conditionId;
        return;
      }

      const oldNodeId = oldNodeIdByMethodId.get(oldMethodId);
      assert(oldNodeId != null, 'Expected every method to have a node ID');

      const newMethodId = newMethodIdByNodeId.get(oldNodeId);
      // The method does not exist in the new flowchart
      if (newMethodId == null) {
        return;
      }

      // The same node exists in the new flowchart, but there's a new version
      preservedConditionIdByNewMethodId[newMethodId] = conditionId;
    });

    return preservedConditionIdByNewMethodId;
  });
}

/**
 * Partitions serialized method plans into one or more condition payloads keyed by condition id.
 *
 * For persisted plans, each PMDV must map to its own condition id in `conditionIdsByPmdvId`, so this
 * returns one entry per method plan with that method's id and `templateMethodVersionId`.
 * For new unsaved conditions (no ids yet), this returns one id-less entry per method plan (i.e., per PMDV).
 */
function fanOutMethodPlansByConditionId(
  condition: ConditionGroup,
  methodPlans: Array<NonNullable<ProcedureRunPlanConditionREST['methodPlan']>>
): Array<{
  id?: string;
  templateMethodVersionId?: string;
  methodPlan: NonNullable<ProcedureRunPlanConditionREST['methodPlan']>;
}> {
  const conditionIdsByMethodVersionId = condition.conditionIdsByPmdvId;
  // An empty object indicates a new, unpersisted condition.
  if (Object.keys(conditionIdsByMethodVersionId).length === 0) {
    return methodPlans.map((methodPlan) => ({
      templateMethodVersionId: methodPlan.methodVersionId,
      methodPlan,
    }));
  }

  // TODO: Update serialization once procedure run plans support sparse conditions
  const seenConditionIds = new Set<string>();
  return methodPlans.map((methodPlan) => {
    const conditionId = conditionIdsByMethodVersionId[methodPlan.methodVersionId];
    assert(
      conditionId != null,
      `Expected condition ID for methodVersionId ${methodPlan.methodVersionId} in conditionIdsByPmdvId`
    );
    assert(
      !seenConditionIds.has(conditionId),
      `Expected one condition ID per PMDV within a ConditionGroup, got duplicate condition ID ${conditionId}`
    );
    seenConditionIds.add(conditionId);
    return {
      id: conditionId,
      templateMethodVersionId: methodPlan.methodVersionId,
      methodPlan,
    };
  });
}

export const MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanStudyDesignTabContentsQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...GetStudyDesignFromProcedureRunPlanGraphQL
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
                name
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
              name
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT}
`;

function ProcedureRunPlanDesignTabContents(props: {
  procedureRunPlan: ProcedureRunPlanNode;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const {procedureRunPlan, procedureType} = props;
  const isFlowchartProcedureRunDesignEnabled = shouldShowFlowchartProcedureRunDesign();
  const {shouldShowNewUIOptInBanner, dismissNewUIOptInBanner, isOptOutBannerDismissed, dismissOptOutBanner} =
    useFlowchartProcedureRunDesignPreviewState();
  const persistedPlanDesignState = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[props.studyId]?.procedureRunPlanDesignStateByPlanId[
        procedureRunPlan.id
      ]
  );

  const {
    experimentalInputs: serverInitialExperimentalInputs,
    templateFlowchart,
    conditionGroups: serverInitialConditions,
  } = useMemo(() => getStudyDesignFromProcedureRunPlan(procedureRunPlan), [procedureRunPlan]);
  const templateMethodIds = useMemo(
    () => new Set(templateFlowchart.nodes.map((method) => method.id)),
    [templateFlowchart.nodes]
  );
  const compatiblePersistedPlanDesignState = isCompatiblePersistedPlanDesignState(
    persistedPlanDesignState,
    templateMethodIds
  )
    ? persistedPlanDesignState
    : undefined;
  const initialExperimentalInputs =
    compatiblePersistedPlanDesignState?.experimentalInputs ?? serverInitialExperimentalInputs;

  // Redux captures brand-new conditions before the first save assigns ids,
  // and nothing back-fills those ids into the snapshot afterwards. Refresh
  // missing ids from the server so subsequent saves update rows instead of
  // re-creating them. We match by position (index) because condition names
  // can be renamed between snapshots and are not unique-constrained.
  const initialConditions: Array<ConditionGroup> = useMemo(() => {
    if (compatiblePersistedPlanDesignState?.conditions == null) {
      return serverInitialConditions;
    }
    return compatiblePersistedPlanDesignState.conditions.map((persisted, index) => {
      if (!isDraftConditionGroup(persisted)) {
        return persisted;
      }
      const serverCondition = serverInitialConditions[index];
      return serverCondition != null
        ? {...persisted, conditionIdsByPmdvId: serverCondition.conditionIdsByPmdvId}
        : persisted;
    });
  }, [compatiblePersistedPlanDesignState, serverInitialConditions]);

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null && !isInaccessible(flowchartVersion), 'Expected flowchart version');
  const procedure = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  const procedureId = procedure.id;
  const procedureName = procedure.name;

  // Check if a newer procedure version has been published
  const versionUpdateCheck = useProcedureVersionUpdateCheck(procedureId, flowchartVersion.id);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  // Get context to register the save-before-promote function with the parent
  const {setSavePlanIfUnsavedChanges, setPlanErrors} = useProcedureRunPlanContext();

  // Editable data entry mode state
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    compatiblePersistedPlanDesignState?.dataEntryMode ?? procedureRunPlan.dataEntryMode
  );

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    context,
    methodExecutionLimitState,
  } = useStudyDesignState({
    isPlan: true,
    initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditions: initialConditions,
    createDefaultConditions: createDefaultConditionGroups,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    initialEditMode: true,
    // Default to no method selected for flowchart procedure run design
    selectFirstMethodInitially: !isFlowchartProcedureRunDesignEnabled,
  });

  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureType}
      showReplicateLimitLabel={true}
      {...planConditionGroupsButtonVariant({
        conditions,
        methodExecutionLimitState,
        procedureType,
      })}
    />
  );

  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;

  // Track whether the user has made any edits since the form was last clean
  // (initial mount or last successful save). We skip the first effect run so
  // that the initial population of state from server/Redux doesn't mark the
  // form dirty
  const [isDirty, setIsDirty] = useState(compatiblePersistedPlanDesignState?.isDirty ?? false);
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setIsDirty(true);
  }, [conditions, experimentalInputs, dataEntryMode]);

  // Pre-promotion there's no per-run condition graph, so planning edges fan the template method
  // graph out across each condition/replicate row to show the execution order in the flowchart.
  const edgeSpecs = useProcedureRunDesignFlowchartEdgeSpecs({
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods: templateFlowchart.nodes,
    procedureEdges: templateFlowchart.edges,
  });

  // Build a map from method ID to method name for error categorization
  const methodIdToName = useMemo(
    () => Object.fromEntries(templateFlowchart.nodes.map((node) => [node.id, node.name])),
    [templateFlowchart.nodes]
  );

  // Propagate validation errors to the parent context so the header can display
  // the error badge and disable the Create button
  const conditionsText = getProcedureRunConditionTextForProcedureType(procedureType, {plural: true});
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  useEffect(() => {
    const errors = collectPlanErrors({
      evaluatedValues: context.evaluatedValues,
      methodExecutionLimitState,
      conditions,
      methodIdToName,
      conditionsText,
      hasNewerVersion: versionUpdateCheck.hasNewerVersion,
      procedureString,
    });

    setPlanErrors(errors);
  }, [
    context.evaluatedValues,
    conditionsText,
    setPlanErrors,
    methodExecutionLimitState,
    conditions,
    methodIdToName,
    versionUpdateCheck.hasNewerVersion,
    procedureString,
  ]);

  // Clear plan errors only on unmount so we don't briefly flash an empty state
  // between dependency-driven re-runs of the effect above.
  useOnMount(() => () => {
    setPlanErrors([]);
  });

  // Create a stable save function reference that can be called from parent
  const handleSaveForPromotion = useCallback(async () => {
    if (editState !== StudyDesignTabEditState.EDITING) {
      return;
    }

    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for plan');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for plan');
    assert(context != null, 'Expected context to be non-null');

    try {
      // Build full experiment plan from current state
      const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
      const namesPerCondition = conditions.map((condition) => condition.name);
      const descriptionsPerCondition = conditions.map((condition) => condition.description);
      const experimentPlan = buildFullExperimentPlan({
        replicatesPerCondition,
        namesPerCondition,
        descriptionsPerCondition,
        numConditions: conditions.length,
        experimentalInputs,
        evaluatedValues: context.evaluatedValues,
      });

      // Serialize to plan-specific REST format (different from procedure runs)
      const conditionsForUpdate = serializePlanConditionsForNetwork(experimentPlan, conditions);

      await updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        dataEntryMode,
      });

      // Mark the form clean. We persist the clean snapshot to Redux explicitly (rather than relying solely on
      // the state-sync effect below) and do so *before* refetching: refetch drives the query through the sync
      // system, which drops it into a loading state that unmounts and remounts this component. On remount the
      // dirty flag is re-hydrated from Redux, so the effect-based dispatch can lose the race and leave Redux
      // holding the stale isDirty=true persisted during the SAVING render, which would re-enable Save immediately
      // after a successful save.
      setIsDirty(false);
      setEditState(StudyDesignTabEditState.EDITING);
      dispatch(
        setProcedureRunPlanDesignState(props.studyId, procedureRunPlan.id, {
          conditions,
          experimentalInputs,
          dataEntryMode,
          isInEditMode: true,
          isDirty: false,
        })
      );

      // Refetch to get updated data
      props.refetch();
    } catch (error) {
      // On error, go back to editing state so user can retry
      setEditState(StudyDesignTabEditState.EDITING);
      throw error;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editState, conditions, experimentalInputs, context, procedureRunPlan.id, props.studyId, dataEntryMode]);

  useEffect(() => {
    dispatch(
      setProcedureRunPlanDesignState(props.studyId, procedureRunPlan.id, {
        conditions,
        experimentalInputs,
        dataEntryMode,
        isInEditMode: editState === StudyDesignTabEditState.EDITING,
        isDirty,
      })
    );
  }, [
    conditions,
    dataEntryMode,
    dispatch,
    editState,
    experimentalInputs,
    isDirty,
    procedureRunPlan.id,
    props.studyId,
  ]);

  useEffect(() => {
    setSavePlanIfUnsavedChanges(handleSaveForPromotion, isDirty);
    return () => {
      setSavePlanIfUnsavedChanges(null);
    };
  }, [handleSaveForPromotion, isDirty, setSavePlanIfUnsavedChanges]);

  /**
   * Handles changing the procedure on a procedure run plan.
   * Updates the plan with the new flowchart version while preserving condition metadata
   * (names, descriptions, replicates) and resetting planned values to the selected procedure's defaults.
   */
  async function handleChangeProcedure(params: ProcedureChangeParams): Promise<void> {
    const defaultInputsForNewProcedure = prepareInitialExperimentalInputs(
      conditions.length,
      params.procedure.methods
    );
    const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(
      defaultInputsForNewProcedure
    );
    const conditionsForUpdate = serializePreservedInputsForNetwork(
      defaultInputsForNewProcedure,
      conditions
    ).map((condition) => ({
      ...condition,
      // Unit operation links are specific to the old procedure and must be cleared.
      replicateInfos: condition.replicateInfos.map((replicateInfo) => ({
        ...replicateInfo,
        sourcePMEIIds: [],
      })),
    }));

    await updateProcedureRunPlan({
      id: procedureRunPlan.id,
      studyId: props.studyId,
      // XOR validation handled by EditProcedureModal - only one of these will be set
      templateProcedureFlowchartVersionId: params.templateProcedureFlowchartVersionId,
      workflowFlowchartConfigVersionId: params.workflowFlowchartConfigVersionId,
      experimentConditions: conditionsForUpdate,
      confirmationMethodByParamVersionId,
    });

    props.refetch();
  }

  /**
   * Compares the current and new procedure versions and preserves inputs for
   * parameters/materials/equipment whose underlying procedure elements have
   * matching stable parameter IDs (as determined by preserveProcedureInputsOnVersionUpdate).
   */
  async function handleApplyProcedureUpdate(): Promise<void> {
    if (
      isApplyingUpdate ||
      !versionUpdateCheck.hasNewerVersion ||
      !versionUpdateCheck.latestPublishedVersion
    ) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      const newFlowchart = versionUpdateCheck.latestPublishedVersion;
      const preservedInputs = preserveProcedureInputsOnVersionUpdate(
        experimentalInputs,
        templateFlowchart,
        newFlowchart,
        conditions.length
      );
      const preservedConditionIdMappings = buildPreservedConditionIdMappings(
        conditions,
        templateFlowchart,
        newFlowchart
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(preservedInputs, conditions).map(
        (condition) => {
          assert(
            condition.templateMethodVersionId != null,
            'Expected templateMethodVersionId for version update condition row'
          );
          const templateMethodVersionId = condition.templateMethodVersionId;
          const preservedConditionId =
            preservedConditionIdMappings[condition.position]?.[templateMethodVersionId];
          return {
            ...condition,
            id: preservedConditionId ?? undefined,
            // Keep existing unit operation links for rows that keep the same condition id.
            // For newly created rows, links reference old PMDVs and must be cleared.
            replicateInfos: condition.replicateInfos.map((replicateInfo) =>
              preservedConditionId != null
                ? replicateInfo
                : {
                    ...replicateInfo,
                    sourcePMEIIds: [],
                  }
            ),
          };
        }
      );

      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlan.id,
          studyId: props.studyId,
          templateProcedureFlowchartVersionId: versionUpdateCheck.isPoW ? undefined : newFlowchart.id,
          workflowFlowchartConfigVersionId: versionUpdateCheck.isPoW ? newFlowchart.id : undefined,
          experimentConditions: conditionsForUpdate,
        }),
        `Updating to latest ${procedureString} version...`
      );

      notifications.success(`Updated to latest ${procedureString} version.`);
      props.refetch();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.error(`Failed to apply ${procedureString} update. Please try again.`);
    } finally {
      setIsApplyingUpdate(false);
    }
  }

  async function handleOpenApplyUpdateModal(): Promise<void> {
    if (!versionUpdateCheck.hasNewerVersion || !versionUpdateCheck.latestPublishedVersion) {
      return;
    }
    if (isInaccessible(flowchartVersion)) {
      return;
    }

    const currentFlowchartVersionId = getFlowchartVersionIdForNavigation(flowchartVersion);

    await legacyRunModal(EditProcedureModal, {
      mode: EditProcedureModalMode.VERSION_UPDATE,
      procedureId,
      flowchartVersionId: currentFlowchartVersionId,
      outdatedFlowchartVersionId: currentFlowchartVersionId,
      currentFlowchartVersionId: versionUpdateCheck.latestPublishedVersion.id,
      studyType: props.studyType,
      procedureType,
      onApplyVersionUpdate: handleApplyProcedureUpdate,
    });
  }

  async function handleOptOutFlowchartDesign(): Promise<void> {
    await optOutOfFlowchartProcedureRunDesign({
      actionOrigin: ActionOrigin.STUDY_RUN_PLAN_DETAILS_PAGE,
      selectedMethodId,
      fallbackMethodId: templateFlowchart.nodes[0]?.id,
      setSelectedMethodId,
      // The method state update could be a no-op, so dismiss the banner to force a render to show the legacy UI.
      onOptedOut: dismissOptOutBanner,
    });
  }

  async function handleRemoveProcedure(): Promise<void> {
    const shouldRemove = (await Promise.resolve(
      modals.confirm(
        `Remove ${procedureString}?`,
        <>
          Removing <strong>{procedureName}</strong> will clear all conditions and factor settings from this
          run plan.
        </>,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Remove',
          rejectText: 'Cancel',
          enableRedesign: true,
          promiseCompatible: true,
        }
      )
    )) as boolean;

    if (!shouldRemove) {
      return;
    }

    const flowchartVersionId = getAccessibleOrInaccessibleId(flowchartVersion);
    const clearLinkedProcedureParams = isProcedureVersionPoW(flowchartVersionId)
      ? {workflowFlowchartConfigVersionId: null}
      : {templateProcedureFlowchartVersionId: null};

    await withLoadingScreen(
      updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        ...clearLinkedProcedureParams,
        experimentConditions: [],
        confirmationMethodByParamVersionId: {},
      }),
      `Removing ${procedureString} from plan...`
    );

    props.refetch();
  }

  const designHeader = (
    <div className={css(studyDesignTabStyles.header)}>
      <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
      <div className={css(studyDesignTabStyles.bannerAndButtons)}>
        {!isFlowchartProcedureRunDesignEnabled && versionUpdateCheck.hasNewerVersion && (
          <PublishedProcedureVersionUpdateBanner
            procedureType={procedureType}
            onOpenUpdateModal={handleOpenApplyUpdateModal}
          />
        )}
        {editState === StudyDesignTabEditState.SAVING ? (
          <StudyDesignTabSavingIndication />
        ) : (
          <StudyDesignTabSaveButton
            isSaveDisabled={!validateConditions(conditions) || !isDirty}
            saveDisabledTooltip={
              !validateConditions(conditions)
                ? (getConditionValidationTooltip(conditions) ?? undefined)
                : 'No changes to save'
            }
            onSave={handleSaveForPromotion}
          />
        )}
      </div>
    </div>
  );

  if (isEmptyFlowchart) {
    return (
      <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
        {versionUpdateCheck.hasNewerVersion && (
          <PublishedProcedureVersionUpdateBanner
            procedureType={procedureType}
            onOpenUpdateModal={handleOpenApplyUpdateModal}
          />
        )}
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
            stringCase: StringCase.LOWER,
            plural: true,
          })} have been configured for this ${getProcedureUIString(
            ProceduresStringKeys.PROCEDURE,
            procedureType,
            {stringCase: StringCase.LOWER}
          )}.`}
        />
      </div>
    );
  }

  // If the flowchart procedure run design is not enabled, we should have a selected method id
  if (!isFlowchartProcedureRunDesignEnabled) {
    assert(
      selectedMethodId != null,
      'Expected selected method id to be non-null for non-flowchart (legacy) procedure run design'
    );
  }

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'ProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      {designHeader}
      {isFlowchartProcedureRunDesignEnabled ? (
        <ProcedureRunDesignFlowchartEditor
          conditions={conditions}
          methods={templateFlowchart.nodes}
          activeMethodId={null} // Active method is not relevant for run plans
          procedureType={procedureType}
          selectedMethodId={selectedMethodId}
          onChangeSelectedMethod={setSelectedMethodId}
          isInEditState={editState === StudyDesignTabEditState.EDITING}
          edgeSpecs={edgeSpecs}
          allowEditingPersistedConditions={true}
          topBanner={
            !isOptOutBannerDismissed || versionUpdateCheck.hasNewerVersion ? (
              <>
                {!isOptOutBannerDismissed && (
                  <FlowchartProcedureRunDesignOptOutBanner
                    onDismiss={dismissOptOutBanner}
                    onOptOut={handleOptOutFlowchartDesign}
                    procedureType={procedureType}
                  />
                )}
                {versionUpdateCheck.hasNewerVersion && (
                  <PublishedProcedureVersionUpdateBanner
                    procedureType={procedureType}
                    onOpenUpdateModal={handleOpenApplyUpdateModal}
                  />
                )}
              </>
            ) : null
          }
          leftPanelChildren={
            <ProcedureRunDesignEditorLeftSidepanel
              conditions={conditions}
              setConditions={setConditions}
              experimentalInputs={experimentalInputs}
              setExperimentalInputs={setExperimentalInputs}
              editState={editState}
              procedureType={procedureType}
              methodExecutionLimitState={methodExecutionLimitState}
              studyType={props.studyType}
              dataEntryMode={dataEntryMode}
              setDataEntryMode={setDataEntryMode}
              procedureId={procedureId}
              flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
              onRemoveProcedure={handleRemoveProcedure}
              hasUpdateAvailable={versionUpdateCheck.hasNewerVersion}
              onOpenUpdateModal={handleOpenApplyUpdateModal}
              isPlan={true}
            />
          }
          // Render the Design panel contents only once a method is selected: the sidepanel requires a
          // non-null selectedMethodId, and the editor keeps the right panel closed without a selection.
          rightPanelChildren={
            selectedMethodId != null && (
              <ProcedureRunDesignEditorRightSidepanel
                templateFlowchart={templateFlowchart}
                selectedMethodId={selectedMethodId}
                procedureType={procedureType}
                context={context}
                isProcedureRunPlan={true}
              />
            )
          }
        />
      ) : (
        <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
          <FlowchartSidebar
            icons={[
              {
                name: 'note-table',
                onClick: () => {
                  setSidepanelName(PARAMETER_VALUES_TAB_NAME);
                },
              },
              {
                name: 'settings',
                onClick: () => {
                  setSidepanelName(SETTINGS_TAB_NAME);
                },
              },
            ]}
            selectedIconIndex={selectedDesignTabIndex}
            setSelectedIconIndex={setSelectedDesignTabIndex}
          />
          <ContextPanel
            sidepanelName={sidepanelName}
            sidepanelHeaderContent={
              selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
                ? editNumColsButtons
                : undefined
            }
          >
            {selectedDesignTabIndex === 0 ? (
              <>
                {shouldShowNewUIOptInBanner && (
                  <div className={css(studyDesignTabStyles.previewBannerContainer)}>
                    <FlowchartProcedureRunDesignOptInBanner
                      procedureType={procedureType}
                      onDismiss={dismissNewUIOptInBanner}
                      onOptIn={async () => {
                        await optInToFlowchartProcedureRunDesign(ActionOrigin.STUDY_RUN_PLAN_DETAILS_PAGE);
                        // Clear selected method ID, so we don't start with right sidepanel open
                        // Old UI requires a non-null selected method ID
                        setSelectedMethodId(null);
                      }}
                    />
                  </div>
                )}
                <ParameterValuesTabContents
                  methods={templateFlowchart.nodes}
                  procedureFlowchart={templateFlowchart}
                  selectedMethodId={selectedMethodId!} // We assert this is non-null above
                  onChangeSelectedMethod={setSelectedMethodId}
                  procedureType={procedureType}
                  context={context}
                />
              </>
            ) : (
              <>
                <SettingsTabContents
                  conditions={conditions}
                  setConditions={setConditions}
                  experimentalInputs={experimentalInputs}
                  setExperimentalInputs={setExperimentalInputs}
                  locked={editState !== StudyDesignTabEditState.EDITING}
                  procedureType={procedureType}
                  methodExecutionLimitState={methodExecutionLimitState}
                  studyType={props.studyType}
                  dataEntryMode={dataEntryMode}
                  estimateResult={{stale: false, executionPlan: null}}
                  setDataEntryMode={setDataEntryMode}
                  isPlan={true}
                />
                <SelectedProcedureEditor
                  isEditable={editState === StudyDesignTabEditState.EDITING}
                  procedureId={procedureId}
                  flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
                  procedureType={procedureType}
                  studyType={props.studyType}
                  onProcedureChange={handleChangeProcedure}
                />
              </>
            )}
          </ContextPanel>
        </div>
      )}
    </div>
  );
}

export default function QueryingProcedureRunPlanDesignTabContents({
  procedureRunPlanId,
  studyType,
  studyId,
  procedureType,
}: {
  procedureRunPlanId: string;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunPlanStudyDesignTabContentsQuery,
    MultiProcedureRunPlanStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {
    variables: {procedureRunPlanId},
    // Render cached data during refetch so attaching a procedure keeps the empty tab (and its loading
    // overlay) mounted until sync completes, instead of flashing the throbber below.
    renderCachedDataWhenRefetching: true,
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data, refetch} = result;
  if (isInaccessible(data.procedureRunPlan)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRunPlan}
        modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
        displayErrorText="This procedure run plan is no longer accessible."
      />
    );
  }

  // A procedure-less plan (no linked flowchart version) renders the empty design editor where the
  // user selects a procedure to attach.
  if (
    data.procedureRunPlan.templateProcedureFlowchartVersion == null &&
    data.procedureRunPlan.procedureFlowchartConfigVersionProxy == null
  ) {
    if (!shouldShowFlowchartProcedureRunDesign()) {
      return (
        <div className={css(studyDesignTabStyles.procedurelessPlanErrorBannerContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="The flowchart-based procedure run design UI must be enabled to view a run plan without a linked procedure version."
          />
        </div>
      );
    }
    return (
      <EmptyProcedureRunPlanDesignTabContents
        procedureRunPlanId={data.procedureRunPlan.id}
        studyId={studyId}
        studyType={studyType}
        procedureType={procedureType}
        refetch={refetch}
      />
    );
  }

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(data.procedureRunPlan);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText="The design of this procedure run plan is not accessible."
      />
    );
  }

  return (
    <ProcedureRunPlanDesignTabContents
      procedureRunPlan={data.procedureRunPlan}
      studyType={studyType}
      studyId={studyId}
      procedureType={procedureType}
      refetch={refetch}
    />
  );
}
